<script setup>
import { ref, onMounted, computed, watch, nextTick } from "vue";
import { Button, Icon } from "../../components";
import useChatWidget from "../stores/chatWidget";
import Banner from "./Banner.vue";
import Welcome from "./Welcome.vue";
import Service from "./Service.vue";
import Staff from "./Staff.vue";
import Date from "./Date.vue";
import Account from "./Account.vue";
import Time from "./Time.vue";
import Overview from "./Overview.vue";

const $chatWidget = useChatWidget();
const $config = $chatWidget.getConfig;
const $globalSettings = computed(() => $chatWidget.getGlobalSettings());
const $setting = $chatWidget.getSettings;
const $customizer = $chatWidget.getCustomizer;
const $payload = computed(() => $chatWidget.getPayload());
const isCompletedAllSteps = ref(false);
console.log($customizer);
const currentStep = computed(() => {
  const steps = [
    {
      name: "service",
      component: Service,
      priority: $setting.service.priority,
    },
    {
      name: "staff",
      component: Staff,
      priority: $setting.staff.priority,
    },
    {
      name: "date",
      component: Date,
      priority: $setting.date.priority,
    },
    {
      name: "time",
      component: Time,
      priority: $setting.time.priority,
    },
  ];

  if ($globalSettings.value?.customer?.customer_id == undefined) {
    steps.push({
      name: "account",
      component: Account,
      priority: $setting.account.priority,
    });
  }

  return steps.sort((a, b) => a.priority - b.priority);
});

const showComponent = computed(() => (componentName) => {
  if (!$payload.value.consent) {
    return false;
  }

  const stepIndex = currentStep.value.findIndex(
    (step) => step.name === componentName
  );

  if (stepIndex === 0 && $payload.value.consent) return true;
  if (stepIndex === -1) return false;

  const previousStep = currentStep.value[stepIndex - 1];
  return $payload.value[previousStep.name];
});

onMounted(async () => {
  await $chatWidget.fetchGlobalSettings();
});

const triggerChatWidget = () => {
  const tempConfig = $config;
  tempConfig.isActive = true;
  tempConfig.isWelcomeActive = true;
  $chatWidget.updateConfig(tempConfig);
};

const scrollToComponent = (componentName) => {
  nextTick(() => {
    const component = document.querySelector(
      `#prebook-chat-component-${componentName}`
    );

    component?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });

    // if (widgetContent && component) {
    //   widgetContent.scrollTo({
    //     top: component.offsetTop,
    //     behavior: "smooth",
    //   });
    // }
  });
};

watch(
  $payload,
  (newPayload) => {
    if (
      newPayload?.consent &&
      newPayload?.date &&
      newPayload?.service &&
      newPayload?.staff &&
      newPayload?.time
    ) {
      if ($globalSettings.value?.customer?.customer_id) {
        isCompletedAllSteps.value = true;
        scrollToComponent("overview");
      } else {
        isCompletedAllSteps.value = false;
        currentStep.value.forEach((step) => {
          if (newPayload.step == step.priority) {
            setTimeout(() => {
              scrollToComponent(step.name);
            }, 150);
          }
        });
      }
    } else {
      isCompletedAllSteps.value = false;
      currentStep.value.forEach((step) => {
        if (newPayload.step == step.priority) {
          setTimeout(() => {
            scrollToComponent(step.name);
          }, 150);
        }
      });
    }

    // if (newPayload?.consent && !newPayload?.consent_scrolled) {
    //   scrollToComponent("welcome");
    //   newPayload.consent_scrolled = true;
    //   $chatWidget.updatePayload(newPayload);
    // }

    // currentStep.value.forEach((step) => {
    //   if (newPayload[step.name] && !newPayload[`${step.name}_scrolled`]) {
    //     scrollToComponent(step.name);
    //     // Mark as scrolled to prevent multiple scrolls
    //     newPayload[`${step.name}_scrolled`] = true;
    //     $chatWidget.updatePayload(newPayload);
    //   }
    // });
  },
  { deep: true }
);

let widget_position_class = "";

switch ($customizer.settings.button_position) {
  case "Right bottom":
    widget_position_class = "right-[10px] bottom-[10px]";
    break;
  case "Right top":
    widget_position_class = "right-[10px] top-[40px]";
    break;
  case "Left bottom":
    widget_position_class = "left-[10px] bottom-[10px]";
    break;
  case "Left top":
    widget_position_class = "left-[10px] top-[40px]";
    break;
}

let widget_btn_style = `padding: 12px 22px !important; border-width: 2px !important; border-radius: ${$customizer.design.button_border_radius}px !important;`;

if ($customizer.design.button_background_style == "Filled") {
  widget_btn_style += ` background-color:${$customizer.design.button_background_color}; border-color:${$customizer.design.button_background_color};`;
} else {
  widget_btn_style += ` background-color:#ffffff; border-color:${$customizer.design.button_background_color};`;
}
console.log(widget_btn_style);
let widget_btn_text_style = `color:${$customizer.design.button_text_color};`;

switch ($customizer.design.button_text_weight) {
  case "Medium":
    widget_btn_text_style += " font-weight: 500 !important; ";
    break;
  case "SemiBold":
    widget_btn_text_style += " font-weight: 600 !important; ";
    break;
  case "Bold":
    widget_btn_text_style += " font-weight: 700 !important; ";
    break;
}
console.log(widget_btn_text_style);
</script>

<template>
  <template v-if="$globalSettings.addons?.chat_widget">
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform -translate-y-4 opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform -translate-y-4 opacity-0"
    >
      <div
        v-if="!$config.isActive"
        class="fixed z-[999]"
        :class="widget_position_class"
      >
        <Button
          id="prebook-chat-widget-button"
          :buttonStyle="widget_btn_style"
          @click="triggerChatWidget"
        >
          <span class="text-[18px]" :style="widget_btn_text_style">{{
            $customizer.settings.button_text
          }}</span>
        </Button>
      </div>
    </Transition>
    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="transform -translate-y-4 opacity-0"
      enter-to-class="transform translate-y-0 opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="transform translate-y-0 opacity-100"
      leave-to-class="transform -translate-y-4 opacity-0"
    >
      <div
        v-if="$config.isActive"
        class="fixed w-[375px] border-solid border-2 border-gray-200 bg-white z-[999] rounded-[10px] overflow-hidden"
        :class="widget_position_class"
      >
        <Banner v-if="$config.isBannerActive" />
        <div class="relative">
          <div
            id="widget-content"
            class="max-h-[300px] overflow-scroll bg-white pt-[10px] mt-[-5px] rounded-[10px]"
          >
            <Welcome v-if="$config.isWelcomeActive" />
            <div v-for="step in currentStep" :key="step.name">
              <component
                :is="step.component"
                v-if="showComponent(step.name)"
                :key="step.name"
              />
            </div>
            <Overview v-if="isCompletedAllSteps" />
          </div>
        </div>
      </div>
    </Transition>
  </template>
</template>
